import React, { useState, useContext, memo, useMemo, useEffect, useCallback, useRef } from "react";
import { LinearProgress } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { appPath, modelServiceLayout, modelsPath } from "helpers/routePaths";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import useModelTaskDetail from "./hooks/useModelTaskDetail";
import useDuplicateTask from "./hooks/useDuplicateTask";
import useTaskRowValidationErrors from "./hooks/useTaskRowValidationErrors";
import useTaskRowContextSync from "./hooks/useTaskRowContextSync";
import useCellTooltipSync from "./hooks/useCellTooltipSync";
import withMount from "components/HOC/withMount";
import { TaskContext } from "contexts/TaskDetailContext";
import { ModelContext } from "contexts/ModelDetailContext";
import TaskRowStatusIcons from "./TaskRowStatusIcons";
import ModelTaskRowContent from "./ModelTaskRowContent";

const useStyles = makeStyles()(() => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
}));

const ModelTaskRow = ({
	data,
	classes,
	index,
	columns,
	row,
	onRequestDelete,
	modelId,
	totalTaskCount,
	handleCopy,
	handleCopyTaskQuestion,
	customCaptions,
	access,
	fetchData,
	originalRow,
	updatedFieldFunc,
	rowIndex,
	isExpanded,
	onExpandedChange,
	showSelection,
	isRowSelected,
	onSelectedChange,
	isRecentlyChanged,
}) => {
	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const shouldHandleClick = useRef(true);
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { classes: rowClasses, cx } = useStyles();
	const toggle = isExpanded;

	const [taskState, CtxDispatch] = useContext(TaskContext);
	const [state, ModelCtxDispatch] = useContext(ModelContext);
	const {
		taskError,
		taskInfo,
		stageList,
		hasTools,
		hasPermits,
		hasImages,
		hasParts,
		hasDocuments,
		hasSafetyCritical,
		hasQuestions,
	} = taskState;
	const toolTipColumn = ["intervals", "zones", "stages", "roles", "notes"];

	const {
		customCaptions: { toolPlural, partPlural, permitPlural, questionPlural, safetyCritical },
	} =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	let rows = {
		...row,
		logo: (
			<TaskRowStatusIcons
				taskError={taskError}
				toggle={toggle}
				hasSafetyCritical={hasSafetyCritical}
				hasParts={hasParts}
				hasTools={hasTools}
				hasPermits={hasPermits}
				hasImages={hasImages}
				hasQuestions={hasQuestions}
				hasDocuments={hasDocuments}
				toolPlural={toolPlural}
				partPlural={partPlural} permitPlural={permitPlural}
				questionPlural={questionPlural}
				safetyCritical={safetyCritical}
			/>
		),
	};

	const navigate = useNavigate();

	const {
		task: singleTask,
		error: taskDetailError,
		setTask: setSingleTask,
	} = useModelTaskDetail({ taskId: row.id, enabled: toggle });

	useEffect(() => {
		if (singleTask) CtxDispatch({ type: "SET_TASK_DETAIL", payload: singleTask });
	}, [CtxDispatch, singleTask]);

	useEffect(() => {
		if (taskDetailError) dispatch(showError(taskDetailError.message || t("message.somethingWentWrong")));
	}, [dispatch, t, taskDetailError]);

	const onHandleTableExpand = useCallback(
		(tg, rowId) => {
			if (!onExpandedChange(rowId, tg)) return;
			shouldHandleClick.current = true;
		},
		[onExpandedChange]
	);

	const { duplicating, handleDuplicate } = useDuplicateTask({
		modelId,
		totalTaskCount,
		ModelCtxDispatch,
		updatedFieldFunc,
		fetchData,
	});

	useTaskRowContextSync({ row, toggle, CtxDispatch });
	useTaskRowValidationErrors({ row, originalRow, taskInfo, stageList, customCaptions, CtxDispatch });
	const handleTooltip = useCellTooltipSync(rows.id);

	const handleDotMenuClick = (e) => {
		// e.stopPropagation();
		shouldHandleClick.current = false;
		setAnchorEl(anchorEl === e.currentTarget ? null : e.currentTarget);
		setSelectedData(anchorEl === e.currentTarget ? null : index);
	};

	const disableLink =
		typeof taskInfo?.stageCount === "number"
			? taskInfo.stageCount === 0
			: !row?.stages;

	const taskRow = useMemo(() => {
		return (
			<ModelTaskRowContent
				rowIndex={rowIndex}
				shouldHandleClick={shouldHandleClick}
				onHandleTableExpand={onHandleTableExpand}
				toggle={toggle}
				isRecentlyChanged={isRecentlyChanged}
				rows={rows}
				row={row}
				showSelection={showSelection}
				isRowSelected={isRowSelected}
				onSelectedChange={onSelectedChange}
				columns={columns}
				classes={classes}
				cx={cx}
				toolTipColumn={toolTipColumn}
				disableLink={disableLink}
				onTooltipOpen={handleTooltip}
				onNameClick={() =>
					navigate(`${appPath}${modelsPath}/${modelId}${modelServiceLayout}`, {
						state: { ModelVersionTaskID: rows.id },
					})
				}
				access={access}
				index={index}
				selectedData={selectedData}
				anchorEl={anchorEl}
				data={data}
				state={state}
				customCaptions={customCaptions}
				onDotMenuClick={handleDotMenuClick}
				onClickAway={() => {
					setAnchorEl(null);
					setSelectedData(null);
				}}
				onDuplicate={() => handleDuplicate(rows)}
				onCopy={() => handleCopy(rows.id)}
				onCopyTaskQuestion={() => handleCopyTaskQuestion(rows.id)}
				onSwitchToServiceLayout={() =>
					navigate(`${appPath}${modelsPath}/${modelId}${modelServiceLayout}`, {
						state: { ModelVersionTaskID: rows.id },
					})
				}
				onRemove={() => onRequestDelete(rows.id)}
				singleTask={singleTask}
				setSingleTask={setSingleTask}
				originalRow={originalRow}
			/>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		toggle,
		totalTaskCount,
		row,
		anchorEl,
		singleTask,
		selectedData,
		index,
		taskState,
		columns,
		showSelection,
		isRowSelected,
		isRecentlyChanged,
	]);

	return (
		<>
			{duplicating && <LinearProgress className={rowClasses.loading} />}
			{taskRow}
		</>
	);
};

export default memo(withMount(ModelTaskRow));
